import { h, forceUpdate } from "@stencil/core";
import { computeFilter, itemHasCheckbox } from "./helpers";
import { fromGxImageToURL } from "./genexus-implementation";
import { removeTreeViewItems, scrollIntoVisibleId, scrollIntoVisiblePath } from "./utils";
import { reloadItems } from "./reload-items";
import { updateItemProperty } from "./update-item-property";
const ROOT_ID = null;
const DEFAULT_DRAG_DISABLED_VALUE = false;
const DEFAULT_DROP_DISABLED_VALUE = false;
const DEFAULT_CLASS_VALUE = "tree-view-item";
const DEFAULT_EDITABLE_ITEMS_VALUE = true;
const DEFAULT_EXPANDED_VALUE = false;
const DEFAULT_INDETERMINATE_VALUE = false;
const DEFAULT_LAZY_VALUE = false;
const DEFAULT_ORDER_VALUE = 0;
const DEFAULT_SELECTED_VALUE = false;
// There are a filter applied and, if the type is "caption" or
// "metadata", the filter property must be set
const treeViewHasFilters = (filterType, filter) => filterType !== "none" &&
    ((filterType !== "caption" && filterType !== "metadata") ||
        (filter != null && filter.trim() !== ""));
const defaultRenderItem = (itemModel, treeState, treeHasFilter, lastItem, level) => (treeState.filterType === "none" || itemModel.render !== false) && (h("ch-tree-view-item", { key: itemModel.id, id: itemModel.id, caption: itemModel.caption, checkbox: itemModel.checkbox ?? treeState.checkbox, checked: itemModel.checked ?? treeState.checked, class: itemModel.class, disabled: itemModel.disabled, downloading: itemModel.downloading, dragDisabled: itemModel.dragDisabled ?? treeState.dragDisabled, dropDisabled: itemModel.dropDisabled ?? treeState.dropDisabled, editable: itemModel.editable ?? treeState.editableItems, expanded: itemModel.expanded, expandableButton: itemModel.expandableButton ?? treeState.expandableButton, expandOnClick: treeState.expandOnClick, indeterminate: itemModel.indeterminate, lastItem: lastItem, lazyLoad: itemModel.lazy, leaf: itemModel.leaf, leftImgSrc: itemModel.leftImgSrc, level: level, metadata: itemModel.metadata, rightImgSrc: itemModel.rightImgSrc, selected: itemModel.selected, showLines: treeState.showLines, toggleCheckboxes: itemModel.toggleCheckboxes ?? treeState.toggleCheckboxes }, !itemModel.leaf &&
    itemModel.items != null &&
    itemModel.items.map((subModel, index) => defaultRenderItem(subModel, treeState, treeHasFilter, treeState.showLines !== "none" &&
        // If there is a filter applied in the current list, use the
        // lastItemId value to calculate the last item
        (treeHasFilter && itemModel.lastItemId !== undefined
            ? subModel.id === itemModel.lastItemId
            : index === itemModel.items.length - 1), level + 1))));
const GXRenderItem = (itemModel, treeState, treeHasFilter, lastItem, level) => (treeState.filterType === "none" || itemModel.render !== false) && (h("ch-tree-view-item", { key: itemModel.id, id: itemModel.id, caption: itemModel.caption, checkbox: itemModel.checkbox ?? treeState.checkbox, checked: itemModel.checked ?? treeState.checked, class: itemModel.class, downloading: itemModel.downloading, dragDisabled: itemModel.dragEnabled != null
        ? !itemModel.dragEnabled
        : treeState.dragDisabled, dropDisabled: itemModel.dropEnabled != null
        ? !itemModel.dropEnabled
        : treeState.dropDisabled, editable: itemModel.editable ?? treeState.editableItems, expanded: itemModel.expanded, expandOnClick: treeState.expandOnClick, indeterminate: itemModel.indeterminate, lastItem: lastItem, lazyLoad: itemModel.lazy, leaf: itemModel.leaf, leftImgSrc: fromGxImageToURL(itemModel.leftImage, treeState.gxSettings, treeState.gxImageConstructor), level: level, metadata: itemModel.metadata, selected: itemModel.selected, showLines: treeState.showLines, toggleCheckboxes: itemModel.toggleCheckboxes ?? treeState.toggleCheckboxes }, !itemModel.leaf &&
    itemModel.items != null &&
    itemModel.items.map((subModel, index) => GXRenderItem(subModel, treeState, treeHasFilter, treeState.showLines !== "none" &&
        // If there is a filter applied in the current list, use the
        // lastItemId value to calculate the last item
        (treeHasFilter && itemModel.lastItemId !== undefined
            ? subModel.id === itemModel.lastItemId
            : index === itemModel.items.length - 1), level + 1))));
const defaultSortItemsCallback = (subModel) => {
    subModel.sort((a, b) => {
        if (a.order < b.order) {
            return -1;
        }
        if (a.order > b.order) {
            return 0;
        }
        return a.caption <= b.caption ? -1 : 0;
    });
};
export class ChTreeViewRender {
    constructor() {
        this.waitDropProcessing = false;
        this.checkbox = false;
        this.checked = false;
        this.checkDroppableZoneCallback = undefined;
        this.cssClass = "tree-view";
        this.dragDisabled = DEFAULT_DRAG_DISABLED_VALUE;
        this.dropDisabled = DEFAULT_DROP_DISABLED_VALUE;
        this.dropItemsCallback = undefined;
        this.editableItems = DEFAULT_EDITABLE_ITEMS_VALUE;
        this.expandableButton = "decorative";
        this.expandOnClick = true;
        this.filter = undefined;
        this.filterDebounce = 250;
        this.filterList = [];
        this.filterOptions = {};
        this.filterType = "none";
        this.gxImageConstructor = undefined;
        this.gxSettings = undefined;
        this.lazyLoadTreeItemsCallback = undefined;
        this.modifyItemCaptionCallback = undefined;
        this.multiSelection = false;
        this.renderItem = defaultRenderItem;
        this.showLines = "none";
        this.sortItemsCallback = defaultSortItemsCallback;
        this.toggleCheckboxes = false;
        this.treeModel = [];
        this.useGxRender = false;
    }
    // UI Models
    #flattenedTreeModel = new Map();
    #flattenedCheckboxTreeModel = new Map();
    #selectedItems = new Set();
    #selectedChangeScheduled = false;
    #checkedChangeScheduled = false;
    #rootNode;
    // Filters info
    #applyFilters = false;
    #immediateFilter;
    #filterTimeout;
    #filterListAsSet;
    // Refs
    #treeRef;
    el;
    handleFilterChange() {
        if (this.filterType === "caption" || this.filterType === "metadata") {
            this.#scheduleFilterProcessing();
        }
    }
    handleFilterDebounceChange() {
        if (this.filterType === "caption" || this.filterType === "metadata") {
            this.#scheduleFilterProcessing();
        }
    }
    handleFilterListChange() {
        // Use a Set to efficiently check for ids
        this.#filterListAsSet = new Set(this.filterList);
        if (this.filterType === "list") {
            this.#scheduleFilterProcessing();
        }
    }
    handleFilterOptionsChange() {
        this.#scheduleFilterProcessing();
    }
    handleFilterTypeChange() {
        this.#scheduleFilterProcessing();
    }
    handleMultiSelectionChange(newMultiSelection) {
        // MultiSelection is disabled. We must select the last updated item
        if (!newMultiSelection) {
            this.#removeAllSelectedItemsExceptForTheLast(this.#selectedItems);
        }
    }
    handleTreeModelChange() {
        this.#flattenModel();
    }
    /**
     * Fired when the checked items change.
     * This event does take into account the currently filtered items.
     */
    checkedItemsChange;
    /**
     * Fired when an element displays its contextmenu.
     */
    itemContextmenu;
    /**
     * Fired when the user interacts with an item in a way that its reference
     * must be opened.
     */
    itemOpenReference;
    /**
     * Fired when the selected items change.
     * This event can be fired by the following conditions:
     *   1. A user changes the selected items interacting with the Tree View.
     *
     *   2. The `multiSelection` value is changed from `true` to `false`.
     *
     *   3. A selected item is no longer rendered because it does not satisfies a
     *      filter condition.
     *
     *   4. TODO: The `treeModel` property is updated and contains different selected
     *      items. Even if it does not contains different selected items, this
     *      event is fired because the selected items can have a different path
     *      than before the `treeModel` update.
     *
     *   5. The `updateItemsProperties` method is executed, changing the item
     *      selection.
     *
     *   6. A selected item is removed.
     *
     *   7. TODO: A selected item is moved into a new parent with drag and drop.
     *      In this case, since the detail of the event contains the information
     *      of the parent, this event must be fired to update the information.
     *
     *   8. Executing `scrollIntoVisible` method and updating the selected value
     *      of the scrolled item.
     *
     *   9. TODO: An external item is dropped into the Tree View and the item is
     *      selected.
     *
     *  10. TODO: Lazy loading content that has selected items?
     *
     * Thing that does not fire this event:
     *   - TODO: Renaming a selected item.
     *
     *   - TODO: Applying a filter that keeps all selected items rendered.
     */
    selectedItemsChange;
    /**
     * Given the drop accepting, the data transfer info and the external items,
     * it process the drops of the items in the tree.
     */
    async dropItems(acceptDrop, dataTransferInfo, items) {
        if (!acceptDrop) {
            return;
        }
        const newParentId = dataTransferInfo.newContainer.id;
        const newParentUIModel = this.#flattenedTreeModel.get(newParentId).item;
        // Only move the items to the new parent, keeping the state
        if (dataTransferInfo.dropInTheSameTree) {
            // Add the UI models to the new container and remove the UI models from
            // the old containers
            dataTransferInfo.draggedItems.forEach(this.#moveItemToNewParent(newParentUIModel));
            // When the selected items are moved, the tree must update its internal
            // state to not have undefined references
            if (dataTransferInfo.draggingSelectedItems) {
                this.#scheduleSelectedItemsChange();
            }
        }
        // Add the new items
        else {
            if (items == null) {
                return;
            }
            // Add new items to the parent
            newParentUIModel.items.push(...items);
            // Flatten the new UI models
            items.forEach(this.#flattenItemUIModel(newParentUIModel));
        }
        this.#sortItems(newParentUIModel.items);
        // Open the item to visualize the new subitems
        newParentUIModel.expanded = true;
        // Re-sync checked items
        this.#scheduleCheckedItemsChange();
        // Update filters
        this.#scheduleFilterProcessing();
        // Force re-render
        forceUpdate(this);
    }
    /**
     * Given a list of ids, it returns an array of the items that exists in the
     * given list.
     */
    async getItemsInfo(itemsId) {
        return this.#getItemsInfo(itemsId);
    }
    #getItemsInfo = (itemsId) => {
        const treeViewItemsInfo = [];
        itemsId.forEach(itemId => {
            const itemUIModel = this.#flattenedTreeModel.get(itemId);
            if (itemUIModel) {
                treeViewItemsInfo.push(itemUIModel);
            }
        });
        return treeViewItemsInfo;
    };
    /**
     * Given an item id, an array of items to add, the download status and the
     * lazy state, updates the item's UI Model.
     */
    async loadLazyContent(itemId, items, downloading = false, lazy = false) {
        const itemToLazyLoadContent = this.#flattenedTreeModel.get(itemId).item;
        // Establish that the content was lazy loaded
        itemToLazyLoadContent.downloading = downloading;
        itemToLazyLoadContent.lazy = lazy;
        // Check if there is items to add
        if (items == null) {
            return;
        }
        // @todo What happens in the server when dropping items on a lazy node?
        itemToLazyLoadContent.items = items;
        this.#sortItems(itemToLazyLoadContent.items);
        this.#flattenSubModel(itemToLazyLoadContent);
        // Re-sync checked items
        this.#scheduleCheckedItemsChange();
        // Update filters
        this.#scheduleFilterProcessing();
        // Force re-render
        forceUpdate(this);
    }
    /**
     * Given a list of ids, removes the items and their children in the tree.
     */
    async removeItems(items) {
        const removeItemsResult = removeTreeViewItems(items, this.#flattenedTreeModel, this.#flattenedCheckboxTreeModel, this.#selectedItems);
        if (!this.#treeHasFilters()) {
            // Update selected items
            if (removeItemsResult.atLeastOneSelected) {
                this.#updateSelectedItems();
            }
            // Re-sync checked items
            if (removeItemsResult.atLeastOneCheckbox) {
                this.#scheduleCheckedItemsChange();
            }
        }
        // Force re-render
        if (removeItemsResult.atLeastOneElement) {
            forceUpdate(this);
            // Update filters
            this.#scheduleFilterProcessing("immediate");
        }
    }
    /**
     * Given an item id and the additional properties to update before and after
     * reload, it reloads the items of the `itemId` node by using the
     * `lazyLoadTreeItemsCallback` property.
     */
    async reloadItems(itemId, beforeProperties, afterProperties) {
        const success = await reloadItems(this.el, itemId, this.#flattenedTreeModel, this.lazyLoadTreeItemsCallback, (itemId, items) => this.loadLazyContent(itemId, items), (items) => this.removeItems(items), beforeProperties, afterProperties);
        return success;
    }
    /**
     * Given the path of the item (represent by a sorted array containing all ids
     * from the root to the item) and the additional properties to update after,
     * it displays and scrolls into the item view.
     * The path can also be a string representing the id of the item to scroll
     * into.
     *
     * When using a path, this method will fail if:
     *   - The path does not start from the root element.
     *   - The path contains a cycle.
     *   - The path does not correspond to a valid path on the server:
     *     - One of the item of the path, except for the last one, is a leaf.
     *     - An item in the path does not exists on the server.
     *     - The path has repeated items.
     *     - And so on.
     */
    async scrollIntoVisible(path, afterProperties) {
        const hasOnlyTheItemId = typeof path === "string";
        const success = await (hasOnlyTheItemId
            ? scrollIntoVisibleId(path, this.#flattenedTreeModel)
            : scrollIntoVisiblePath(this.el, path, this.#flattenedTreeModel, this.#rootNode, this.lazyLoadTreeItemsCallback));
        if (!success) {
            return false;
        }
        const itemId = hasOnlyTheItemId ? path : path[path.length - 1];
        // Expand all parent items
        let parentInfo = this.#flattenedTreeModel.get(itemId).parentItem;
        while (parentInfo !== this.#rootNode) {
            parentInfo.expanded = true;
            parentInfo = this.#flattenedTreeModel.get(parentInfo.id).parentItem;
        }
        if (afterProperties) {
            this.updateItemsProperties([itemId], afterProperties);
        }
        forceUpdate(this);
        // Scroll into the itemId view, after rendering has completed
        requestAnimationFrame(() => {
            this.#treeRef.scrollIntoVisible(itemId);
        });
        return true;
    }
    /**
     * This method is used to toggle a tree item by the tree item id/ids.
     *
     * @param treeItemIds An array id the tree items to be toggled.
     * @param expand A boolean indicating that the tree item should be expanded or collapsed. (optional)
     * @returns The modified items after the method was called.
     */
    async toggleItems(treeItemIds, expand) {
        if (!treeItemIds) {
            return [];
        }
        const modifiedTreeItems = [];
        treeItemIds.forEach(treeItemId => {
            const itemInfo = this.#flattenedTreeModel.get(treeItemId).item;
            if (itemInfo) {
                itemInfo.expanded = expand ?? !itemInfo.expanded;
                modifiedTreeItems.push({
                    id: itemInfo.id,
                    expanded: itemInfo.expanded
                });
            }
        });
        // Force re-render
        forceUpdate(this);
        return modifiedTreeItems;
    }
    /**
     * Given a subset of item's properties, it updates all item UI models.
     */
    async updateAllItemsProperties(properties) {
        [...this.#flattenedTreeModel.values()].forEach(itemUIModel => {
            if (properties.expanded != null) {
                itemUIModel.item.expanded = properties.expanded;
            }
            if (properties.checked != null) {
                itemUIModel.item.checked = properties.checked;
                itemUIModel.item.indeterminate = false;
            }
        });
        // Update filters
        if (properties.checked != null) {
            this.#scheduleFilterProcessing();
        }
        forceUpdate(this);
    }
    /**
     * Given a item list and the properties to update, it updates the properties
     * of the items in the list.
     */
    async updateItemsProperties(items, properties) {
        // Set to check if there are new selected items
        const newSelectedItems = new Set(this.#selectedItems);
        // Map to check if there are new items with checkbox
        const newCheckboxItems = new Map(this.#flattenedCheckboxTreeModel);
        items.forEach(itemId => {
            updateItemProperty(itemId, properties, this.#flattenedTreeModel, newSelectedItems, newCheckboxItems, this.checkbox);
        });
        // MultiSelection is disabled. We must select the last updated item
        if (!this.multiSelection) {
            this.#removeAllSelectedItemsExceptForTheLast(newSelectedItems);
        }
        // Update filters if necessary
        if (this.#treeHasFilters()) {
            this.#scheduleFilterProcessing();
        }
        else {
            this.#checkIfThereAreDifferentItemsWithCheckbox(newCheckboxItems);
            this.#checkIfThereAreDifferentSelectedItems(newSelectedItems);
        }
        forceUpdate(this);
    }
    /**
     * Update the information about the valid droppable zones.
     * @param requestTimestamp Time where the request to the server was made. Useful to avoid having old information.
     * @param newContainerId ID of the container where the drag is trying to be made.
     * @param draggedItems Information about the dragged items.
     * @param validDrop Current state of the droppable zone.
     */
    async updateValidDropZone(requestTimestamp, newContainerId, draggedItems, validDrop) {
        this.#treeRef.updateValidDropZone(requestTimestamp, newContainerId, draggedItems, validDrop);
    }
    updateCheckboxValue(event) {
        event.stopPropagation();
        const detail = event.detail;
        const treeItemId = detail.id;
        const itemUIModel = this.#flattenedCheckboxTreeModel.get(treeItemId);
        // In some cases, when the `treeModel` and `checked` properties are updated
        // outside of the tree control, some events are fired with undefined references
        if (!itemUIModel) {
            return;
        }
        const itemInfo = itemUIModel.item;
        itemInfo.checked = detail.checked;
        itemInfo.indeterminate = detail.indeterminate;
        this.#scheduleCheckedItemsChange();
        // Update filters
        if (this.filterType === "checked" || this.filterType === "unchecked") {
            this.#scheduleFilterProcessing();
        }
        // Force re-render
        forceUpdate(this);
    }
    loadLazyChildrenHandler(event) {
        if (!this.lazyLoadTreeItemsCallback) {
            return;
        }
        event.stopPropagation();
        const treeItemId = event.detail;
        const promise = this.lazyLoadTreeItemsCallback(treeItemId);
        event.target.downloading = true;
        promise.then(result => {
            this.loadLazyContent(treeItemId, result);
        });
    }
    handleCaptionModification(event) {
        if (!this.modifyItemCaptionCallback) {
            return;
        }
        event.stopPropagation();
        const itemRef = event.target;
        const itemId = event.detail.id;
        const itemUIModel = this.#flattenedTreeModel.get(itemId);
        const itemInfo = itemUIModel.item;
        const newCaption = event.detail.caption;
        const oldCaption = itemInfo.caption;
        // Optimistic UI: Update the caption in the UI Model before the change is
        // completed in the server
        itemInfo.caption = newCaption;
        // Due to performance reasons, we don't make a shallow copy of the
        // treeModel to force a re-render
        itemRef.caption = newCaption;
        const promise = this.modifyItemCaptionCallback(itemId, newCaption);
        promise.then(status => {
            if (status.success) {
                this.#sortItems(itemUIModel.parentItem.items);
                // Update filters
                this.#scheduleFilterProcessing();
                // Force re-render
                forceUpdate(this);
            }
            else {
                itemRef.caption = oldCaption;
                itemInfo.caption = oldCaption;
                // Do something with the error message
            }
        });
    }
    handleOpenReference(event) {
        event.stopPropagation();
        this.itemOpenReference.emit(event.detail);
    }
    #handleDroppableZoneEnter = (event) => {
        if (!this.checkDroppableZoneCallback) {
            return;
        }
        event.stopPropagation();
        // Suppose the request is made immediately by executing the callback
        const requestTimestamp = new Date().getTime();
        const dropInformation = event.detail;
        const promise = this.checkDroppableZoneCallback(dropInformation);
        promise.then(validDrop => {
            this.updateValidDropZone(requestTimestamp, dropInformation.newContainer.id, dropInformation.draggedItems, validDrop);
        });
    };
    #handleSelectedItemsChange = (event) => {
        event.stopPropagation();
        const itemsToProcess = new Map(event.detail);
        const previousSelectedItems = this.#selectedItems;
        // Remove no longer selected items
        previousSelectedItems.forEach(selectedItemId => {
            const itemUIModel = this.#flattenedTreeModel.get(selectedItemId).item;
            const itemIsStillSelected = itemsToProcess.get(selectedItemId);
            // The item does not need to be added. Remove it from the processed list
            if (itemIsStillSelected) {
                itemUIModel.expanded = itemIsStillSelected.expanded; // Update expanded state
                itemsToProcess.delete(selectedItemId);
            }
            // The item must be un-selected in the UI Model
            else {
                itemUIModel.selected = false;
                previousSelectedItems.delete(selectedItemId);
            }
        });
        // Add new selected items
        itemsToProcess.forEach((newSelectedItemInfo, itemId) => {
            const newSelectedItem = this.#flattenedTreeModel.get(itemId).item;
            newSelectedItem.selected = true;
            newSelectedItem.expanded = newSelectedItemInfo.expanded;
            previousSelectedItems.add(itemId);
        });
        // Queue re-render to avoid issues about synchronization the Virtual DOM
        // with the real DOM
        forceUpdate(this);
        this.#updateSelectedItems();
    };
    #handleExpandedItemChange = (event) => {
        const detail = event.detail;
        const itemInfo = this.#flattenedTreeModel.get(detail.id).item;
        itemInfo.expanded = detail.expanded;
    };
    #handleItemContextmenu = (event) => {
        event.stopPropagation();
        this.itemContextmenu.emit(event.detail);
    };
    #handleItemsDropped = (event) => {
        const dataTransferInfo = event.detail;
        const newContainer = dataTransferInfo.newContainer;
        const newParentId = newContainer.id;
        // Check if the parent exists in the UI Model
        if (!this.#flattenedTreeModel.get(newParentId)) {
            return;
        }
        const draggedItems = dataTransferInfo.draggedItems;
        if (draggedItems.length === 0 || !this.dropItemsCallback) {
            return;
        }
        event.stopPropagation();
        const promise = this.dropItemsCallback(dataTransferInfo);
        this.waitDropProcessing = true;
        promise.then(async (response) => {
            this.dropItems(response.acceptDrop, dataTransferInfo, response.items);
            this.waitDropProcessing = false;
        });
    };
    #removeAllSelectedItemsExceptForTheLast = (currentSelectedItems) => {
        if (currentSelectedItems.size > 1) {
            const selectedItemsArray = [...currentSelectedItems.values()];
            const lastItemIndex = currentSelectedItems.size - 1;
            // Deselect all items except the last
            for (let index = 0; index < lastItemIndex; index++) {
                const itemId = selectedItemsArray[index];
                this.#flattenedTreeModel.get(itemId).item.selected = false;
            }
            // Create a new Set with only the last item
            currentSelectedItems.clear();
            currentSelectedItems.add(selectedItemsArray[lastItemIndex]);
            this.#scheduleSelectedItemsChange();
        }
    };
    #moveItemToNewParent = (newParentUIModel) => (dataTransferInfo) => {
        const itemUIModelExtended = this.#flattenedTreeModel.get(dataTransferInfo.id);
        const item = itemUIModelExtended.item;
        const oldParentItem = itemUIModelExtended.parentItem;
        // Remove the UI model from the previous parent. The equality function
        // must be by index, not by object reference
        oldParentItem.items.splice(oldParentItem.items.findIndex(el => el.id === item.id), 1);
        // Add the UI Model to the new parent
        newParentUIModel.items.push(item);
        // Reference the new parent in the item
        itemUIModelExtended.parentItem = newParentUIModel;
    };
    #flattenSubModel = (model) => {
        const items = model.items;
        if (!items) {
            // Make sure that subtrees don't have an undefined array
            if (model.leaf !== true) {
                model.items = [];
            }
            return;
        }
        this.#sortItems(items);
        items.forEach(this.#flattenItemUIModel(model));
    };
    #flattenItemUIModel = (parentModel) => (item) => {
        this.#flattenedTreeModel.set(item.id, {
            parentItem: parentModel,
            item: item
        });
        // Add the items that have a checkbox in a separate Map
        if (this.#itemHasCheckbox(item)) {
            this.#flattenedCheckboxTreeModel.set(item.id, {
                parentItem: parentModel,
                item: item
            });
        }
        // Make sure the properties are with their default values to avoid issues
        // when reusing DOM nodes
        item.class ||= DEFAULT_CLASS_VALUE;
        item.expanded ??= DEFAULT_EXPANDED_VALUE;
        item.indeterminate ??= DEFAULT_INDETERMINATE_VALUE;
        item.lazy ??= DEFAULT_LAZY_VALUE;
        item.order ??= DEFAULT_ORDER_VALUE;
        item.selected ??= DEFAULT_SELECTED_VALUE;
        if (item.selected) {
            this.#selectedItems.add(item.id);
        }
        this.#flattenSubModel(item);
    };
    #itemHasCheckbox = (item) => itemHasCheckbox(item, this.checkbox);
    #treeHasFilters = () => treeViewHasFilters(this.filterType, this.filter);
    #sortItems = (items) => {
        // Ensure that items are sorted
        if (this.sortItemsCallback) {
            this.sortItemsCallback(items);
        }
    };
    #flattenModel = () => {
        this.#flattenedTreeModel.clear();
        this.#flattenedCheckboxTreeModel.clear();
        this.#selectedItems.clear();
        this.#rootNode = { id: ROOT_ID, caption: ROOT_ID, items: this.treeModel };
        this.#flattenSubModel(this.#rootNode);
        // Re-sync filters
        this.#scheduleFilterProcessing();
        // The model was updated at runtime, so we need to update the references
        // Re-sync selected items
        this.#scheduleSelectedItemsChange();
        // Re-sync checked items
        this.#scheduleCheckedItemsChange();
    };
    #filterSubModel = (item, filterInfo, currentSelectedItems, currentCheckboxItems) => {
        let aSubItemIsRendered = false;
        // Check if a subitem is rendered
        if (item.leaf !== true && item.items != null) {
            let lastItemId = undefined;
            item.items.forEach(subItem => {
                const itemSatisfiesFilter = this.#filterSubModel(subItem, filterInfo, currentSelectedItems, currentCheckboxItems);
                aSubItemIsRendered ||= itemSatisfiesFilter;
                if (itemSatisfiesFilter) {
                    lastItemId = subItem.id;
                }
            });
            item.lastItemId = lastItemId;
        }
        // The current item is rendered if it satisfies the filter condition or a
        // subitem exists that needs to be rendered
        const satisfiesFilter = aSubItemIsRendered || computeFilter(this.filterType, item, filterInfo);
        item.render = satisfiesFilter; // Update item render
        // Update selected and checkbox items
        if (satisfiesFilter && item.id !== ROOT_ID) {
            if (item.selected) {
                currentSelectedItems.add(item.id);
            }
            if (this.#itemHasCheckbox(item)) {
                const itemUIModel = this.#flattenedTreeModel.get(item.id);
                currentCheckboxItems.set(item.id, itemUIModel);
            }
        }
        return satisfiesFilter;
    };
    #scheduleCheckedItemsChange = () => {
        this.#checkedChangeScheduled = true;
    };
    #scheduleSelectedItemsChange = () => {
        this.#selectedChangeScheduled = true;
    };
    #updateSelectedItems = () => {
        const selectedItemsInfo = this.#getItemsInfo([
            ...this.#selectedItems.keys()
        ]);
        this.selectedItemsChange.emit(selectedItemsInfo);
    };
    #updateCheckedItems = () => {
        // New copy of the checked items
        const allItemsWithCheckbox = new Map(this.#flattenedCheckboxTreeModel);
        // Update the checked value if not defined
        allItemsWithCheckbox.forEach(itemUIModel => {
            itemUIModel.item.checked ??= this.checked;
        });
        this.checkedItemsChange.emit(allItemsWithCheckbox);
    };
    #scheduleFilterProcessing = (immediateFilter) => {
        this.#applyFilters = true;
        if (immediateFilter !== undefined) {
            this.#immediateFilter ??= immediateFilter;
        }
    };
    #checkIfThereAreDifferentItemsWithCheckbox = (newCheckboxItems) => {
        if (newCheckboxItems.size !== this.#flattenedCheckboxTreeModel.size) {
            this.#checkedChangeScheduled = true;
        }
        // Check if the items in each Map have the same id
        else {
            this.#flattenedCheckboxTreeModel.forEach((_, itemId) => {
                // Found a value that don't belong to the checkboxItems with filters,
                // schedule checkedItemsChange
                if (!newCheckboxItems.has(itemId)) {
                    // Schedule checkedItemsChange
                    this.#checkedChangeScheduled = true;
                }
            });
        }
        // The previous checkbox items will now be the selected items with filter
        this.#flattenedCheckboxTreeModel = newCheckboxItems;
    };
    #checkIfThereAreDifferentSelectedItems = (newSelectedItems) => {
        if (newSelectedItems.size !== this.#selectedItems.size) {
            this.#selectedChangeScheduled = true;
        }
        // Check if the items in each Set have the same id
        else {
            this.#selectedItems.forEach(itemId => {
                // Found a value that don't belong to the selectedItems with filters,
                // deselect the item
                if (!newSelectedItems.has(itemId)) {
                    this.#flattenedTreeModel.get(itemId).item.selected = false;
                    // Schedule selectedItemsChange
                    this.#selectedChangeScheduled = true;
                }
            });
        }
        // The previous selected items will now be the selected items with filter
        this.#selectedItems = newSelectedItems;
    };
    #updateFilters = () => {
        if (this.filterType === "none") {
            // Check if there are more items with checkbox
            const itemsWithCheckbox = new Map();
            this.#flattenedTreeModel.forEach((itemUIModel, itemId) => {
                if (this.#itemHasCheckbox(itemUIModel.item)) {
                    itemsWithCheckbox.set(itemId, itemUIModel);
                }
            });
            this.#checkIfThereAreDifferentItemsWithCheckbox(itemsWithCheckbox);
            this.#validateCheckedAndSelectedItems();
            return;
        }
        // Remove queued filter processing
        clearTimeout(this.#filterTimeout);
        const processWithDebounce = this.filterDebounce > 0 &&
            (this.filterType === "caption" || this.filterType === "metadata");
        const filterFunction = () => {
            const currentSelectedItems = new Set();
            const currentCheckedItems = new Map();
            this.#filterSubModel({
                id: ROOT_ID,
                caption: ROOT_ID,
                items: this.treeModel
            }, {
                defaultCheckbox: this.checkbox,
                defaultChecked: this.checked,
                filter: this.filter,
                filterOptions: this.filterOptions,
                filterSet: this.#filterListAsSet
            }, currentSelectedItems, currentCheckedItems);
            // It validates if there are differences between the items with checkbox
            // and the selected items. If there are, emit the corresponding updates.
            this.#checkIfThereAreDifferentSelectedItems(currentSelectedItems);
            this.#checkIfThereAreDifferentItemsWithCheckbox(currentCheckedItems);
            this.#validateCheckedAndSelectedItems();
        };
        // Check if should filter with debounce
        if (processWithDebounce && this.#immediateFilter !== "immediate") {
            this.#filterTimeout = setTimeout(() => {
                this.#immediateFilter = undefined;
                filterFunction();
                forceUpdate(this); // After the filter processing is completed, force a re-render
            }, this.filterDebounce);
        }
        // No debounce
        else {
            this.#immediateFilter = undefined;
            filterFunction();
        }
    };
    #validateCheckedAndSelectedItems = () => {
        if (this.#checkedChangeScheduled) {
            this.#updateCheckedItems();
            this.#checkedChangeScheduled = false;
        }
        if (this.#selectedChangeScheduled) {
            this.#selectedChangeScheduled = false;
            // Update the selected items in the ch-tree-view control
            this.#updateSelectedItems();
        }
    };
    #getSelectedItemsCallback = () => {
        const selectedItemsInfo = new Map();
        this.#selectedItems.forEach(itemId => {
            const itemUIModel = this.#flattenedTreeModel.get(itemId);
            const itemInfo = itemUIModel.item;
            selectedItemsInfo.set(itemId, {
                id: itemInfo.id,
                expanded: itemInfo.expanded,
                metadata: itemInfo.metadata,
                parentId: itemUIModel.parentItem.id
            });
        });
        return selectedItemsInfo;
    };
    componentWillLoad() {
        if (this.useGxRender) {
            this.renderItem = GXRenderItem;
        }
        this.#flattenModel();
    }
    componentWillRender() {
        if (!this.#selectedChangeScheduled &&
            !this.#checkedChangeScheduled &&
            !this.#applyFilters) {
            return;
        }
        // If the filters must be applied, we must let the filters decided which
        // are the selected and checked items
        if (this.#applyFilters) {
            this.#updateFilters();
            this.#applyFilters = false;
            return;
        }
        this.#validateCheckedAndSelectedItems();
    }
    render() {
        return (h("ch-tree-view", { class: this.cssClass || null, multiSelection: this.multiSelection, selectedItemsCallback: this.#getSelectedItemsCallback, waitDropProcessing: this.waitDropProcessing, onDroppableZoneEnter: this.#handleDroppableZoneEnter, onExpandedItemChange: this.#handleExpandedItemChange, onItemContextmenu: this.#handleItemContextmenu, onItemsDropped: this.#handleItemsDropped, onSelectedItemsChange: this.#handleSelectedItemsChange, ref: el => (this.#treeRef = el) }, this.treeModel.map((itemModel, index) => this.renderItem(itemModel, this, this.#treeHasFilters(), this.showLines !== "none" && index === this.treeModel.length - 1, 0))));
    }
    static get is() { return "ch-tree-view-render"; }
    static get originalStyleUrls() {
        return {
            "$": ["tree-view-render.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["tree-view-render.css"]
        };
    }
    static get properties() {
        return {
            "checkbox": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want display a checkbox in all items by default."
                },
                "attribute": "checkbox",
                "reflect": false,
                "defaultValue": "false"
            },
            "checked": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want the checkbox to be checked in all items by\ndefault.\nOnly works if `checkbox = true`"
                },
                "attribute": "checked",
                "reflect": false,
                "defaultValue": "false"
            },
            "checkDroppableZoneCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    dropInformation: TreeViewDropCheckInfo\n  ) => Promise<boolean>",
                    "resolved": "(dropInformation: TreeViewDropCheckInfo) => Promise<boolean>",
                    "references": {
                        "TreeViewDropCheckInfo": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewDropCheckInfo"
                        },
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Callback that is executed when an element tries to drop in another item of\nthe tree. Returns whether the drop is valid."
                }
            },
            "cssClass": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A CSS class to set as the `ch-tree-view` element class."
                },
                "attribute": "css-class",
                "reflect": false,
                "defaultValue": "\"tree-view\""
            },
            "dragDisabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the drag operation is disabled in all\nitems by default. If `true`, the items can't be dragged."
                },
                "attribute": "drag-disabled",
                "reflect": false,
                "defaultValue": "DEFAULT_DRAG_DISABLED_VALUE"
            },
            "dropDisabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the drop operation is disabled in all\nitems by default. If `true`, the items won't accept any drops."
                },
                "attribute": "drop-disabled",
                "reflect": false,
                "defaultValue": "DEFAULT_DROP_DISABLED_VALUE"
            },
            "dropItemsCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    dataTransferInfo: TreeViewDataTransferInfo\n  ) => Promise<{ acceptDrop: boolean; items?: TreeViewItemModel[] }>",
                    "resolved": "(dataTransferInfo: TreeViewDataTransferInfo) => Promise<{ acceptDrop: boolean; items?: TreeViewItemModel[]; }>",
                    "references": {
                        "TreeViewDataTransferInfo": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewDataTransferInfo"
                        },
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Callback that is executed when a list of items request to be dropped into\nanother item."
                }
            },
            "editableItems": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the edit operation is enabled in all\nitems by default. If `true`, the items can edit its caption in place."
                },
                "attribute": "editable-items",
                "reflect": false,
                "defaultValue": "DEFAULT_EDITABLE_ITEMS_VALUE"
            },
            "expandableButton": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"action\" | \"decorative\" | \"no\"",
                    "resolved": "\"action\" | \"decorative\" | \"no\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies what kind of expandable button is displayed in the items by\ndefault.\n - `\"expandableButton\"`: Expandable button that allows to expand/collapse\n    the items of the control.\n - `\"decorative\"`: Only a decorative icon is rendered to display the state\n    of the item."
                },
                "attribute": "expandable-button",
                "reflect": false,
                "defaultValue": "\"decorative\""
            },
            "expandOnClick": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if a tree-view-item is expanded on click interaction. If `true`\nthe tree-view-item is expanded on click interaction. If `false`, with\nmouse interaction the tree-view-item will only be expanded on double click."
                },
                "attribute": "expand-on-click",
                "reflect": false,
                "defaultValue": "true"
            },
            "filter": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you determine the expression that will be applied to the\nfilter.\nOnly works if `filterType = \"caption\" | \"metadata\"`."
                },
                "attribute": "filter",
                "reflect": false
            },
            "filterDebounce": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you determine the debounce time (in ms) that the\ncontrol waits until it processes the changes to the filter property.\nConsecutive changes to the `filter` property between this range, reset the\ntimeout to process the filter.\nOnly works if `filterType = \"caption\" | \"metadata\"`."
                },
                "attribute": "filter-debounce",
                "reflect": false,
                "defaultValue": "250"
            },
            "filterList": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "string[]",
                    "resolved": "string[]",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you determine the list of items that will be filtered.\nOnly works if `filterType = \"list\"`."
                },
                "defaultValue": "[]"
            },
            "filterOptions": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "TreeViewFilterOptions",
                    "resolved": "{ autoExpand?: boolean; hideMatchesAndShowNonMatches?: boolean; highlightMatchedItems?: boolean; matchCase?: boolean; regularExpression?: boolean; }",
                    "references": {
                        "TreeViewFilterOptions": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/renders/tree-view/types.ts::TreeViewFilterOptions"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you determine the options that will be applied to the\nfilter."
                },
                "defaultValue": "{}"
            },
            "filterType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "TreeViewFilterType",
                    "resolved": "\"caption\" | \"checked\" | \"list\" | \"metadata\" | \"none\" | \"unchecked\"",
                    "references": {
                        "TreeViewFilterType": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/renders/tree-view/types.ts::TreeViewFilterType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you define what kind of filter is applied to items.\nOnly items that satisfy the filter predicate will be displayed.\n\n| Value       | Details                                                                                        |\n| ----------- | ---------------------------------------------------------------------------------------------- |\n| `checked`   | Show only the items that have a checkbox and are checked.                                      |\n| `unchecked` | Show only the items that have a checkbox and are not checked.                                  |\n| `caption`   | Show only the items whose `caption` satisfies the regex determinate by the `filter` property.  |\n| `metadata`  | Show only the items whose `metadata` satisfies the regex determinate by the `filter` property. |\n| `list`      | Show only the items that are contained in the array determinate by the `filterList` property.  |\n| `none`      | Show all items.                                                                                |"
                },
                "attribute": "filter-type",
                "reflect": false,
                "defaultValue": "\"none\""
            },
            "gxImageConstructor": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(name: string) => any",
                    "resolved": "(name: string) => any",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property is a WA to implement the Tree View as a UC 2.0 in GeneXus."
                }
            },
            "gxSettings": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property is a WA to implement the Tree View as a UC 2.0 in GeneXus."
                },
                "attribute": "gx-settings",
                "reflect": false
            },
            "lazyLoadTreeItemsCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "LazyLoadTreeItemsCallback",
                    "resolved": "(treeItemId: string) => Promise<TreeViewItemModel[]>",
                    "references": {
                        "LazyLoadTreeItemsCallback": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/renders/tree-view/types.ts::LazyLoadTreeItemsCallback"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Callback that is executed when a item request to load its subitems."
                }
            },
            "modifyItemCaptionCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    treeItemId: string,\n    newCaption: string\n  ) => Promise<TreeViewOperationStatusModifyCaption>",
                    "resolved": "(treeItemId: string, newCaption: string) => Promise<TreeViewOperationStatusModifyCaption>",
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "TreeViewOperationStatusModifyCaption": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/renders/tree-view/types.ts::TreeViewOperationStatusModifyCaption"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Callback that is executed when a item request to modify its caption."
                }
            },
            "multiSelection": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want to allow multi selection of the items."
                },
                "attribute": "multi-selection",
                "reflect": false,
                "defaultValue": "false"
            },
            "renderItem": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "(\n    itemModel: TreeViewItemModel | any,\n    treeState: ChTreeViewRender,\n    treeHasFilter: boolean,\n    lastItem: boolean,\n    level: number\n  ) => any",
                    "resolved": "(itemModel: any, treeState: ChTreeViewRender, treeHasFilter: boolean, lastItem: boolean, level: number) => any",
                    "references": {
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        },
                        "ChTreeViewRender": {
                            "location": "global",
                            "id": "global::ChTreeViewRender"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property allows us to implement custom rendering of tree items."
                },
                "defaultValue": "defaultRenderItem"
            },
            "showLines": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "TreeViewLines",
                    "resolved": "\"all\" | \"last\" | \"none\"",
                    "references": {
                        "TreeViewLines": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewLines"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to display the relation between tree items and tree lists using\nlines."
                },
                "attribute": "show-lines",
                "reflect": false,
                "defaultValue": "\"none\""
            },
            "sortItemsCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(subModel: TreeViewItemModel[]) => void",
                    "resolved": "(subModel: TreeViewItemModel[]) => void",
                    "references": {
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Callback that is executed when the treeModel is changed to order its items."
                },
                "defaultValue": "defaultSortItemsCallback"
            },
            "toggleCheckboxes": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want all the children item's checkboxes to be\nchecked when the parent item checkbox is checked, or to be unchecked when\nthe parent item checkbox is unchecked.\nThis attribute will be used in all items by default."
                },
                "attribute": "toggle-checkboxes",
                "reflect": false,
                "defaultValue": "false"
            },
            "treeModel": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "TreeViewItemModel[]",
                    "resolved": "TreeViewItemModel[]",
                    "references": {
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you define the model of the ch-tree-view-render control."
                },
                "defaultValue": "[]"
            },
            "useGxRender": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property is a WA to implement the Tree View as a UC 2.0 in GeneXus."
                },
                "attribute": "use-gx-render",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "waitDropProcessing": {}
        };
    }
    static get events() {
        return [{
                "method": "checkedItemsChange",
                "name": "checkedItemsChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the checked items change.\nThis event does take into account the currently filtered items."
                },
                "complexType": {
                    "original": "Map<string, TreeViewItemModelExtended>",
                    "resolved": "Map<string, TreeViewItemModelExtended>",
                    "references": {
                        "Map": {
                            "location": "global",
                            "id": "global::Map"
                        },
                        "TreeViewItemModelExtended": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/renders/tree-view/types.ts::TreeViewItemModelExtended"
                        }
                    }
                }
            }, {
                "method": "itemContextmenu",
                "name": "itemContextmenu",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when an element displays its contextmenu."
                },
                "complexType": {
                    "original": "TreeViewItemContextMenu",
                    "resolved": "{ id: string; itemRef: HTMLChTreeViewItemElement; metadata: string; contextmenuEvent: PointerEvent; }",
                    "references": {
                        "TreeViewItemContextMenu": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemContextMenu"
                        }
                    }
                }
            }, {
                "method": "itemOpenReference",
                "name": "itemOpenReference",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the user interacts with an item in a way that its reference\nmust be opened."
                },
                "complexType": {
                    "original": "TreeViewItemOpenReferenceInfo",
                    "resolved": "{ id: string; leaf: boolean; metadata: string; }",
                    "references": {
                        "TreeViewItemOpenReferenceInfo": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemOpenReferenceInfo"
                        }
                    }
                }
            }, {
                "method": "selectedItemsChange",
                "name": "selectedItemsChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the selected items change.\nThis event can be fired by the following conditions:\n  1. A user changes the selected items interacting with the Tree View.\n\n  2. The `multiSelection` value is changed from `true` to `false`.\n\n  3. A selected item is no longer rendered because it does not satisfies a\n     filter condition.\n\n  4. TODO: The `treeModel` property is updated and contains different selected\n     items. Even if it does not contains different selected items, this\n     event is fired because the selected items can have a different path\n     than before the `treeModel` update.\n\n  5. The `updateItemsProperties` method is executed, changing the item\n     selection.\n\n  6. A selected item is removed.\n\n  7. TODO: A selected item is moved into a new parent with drag and drop.\n     In this case, since the detail of the event contains the information\n     of the parent, this event must be fired to update the information.\n\n  8. Executing `scrollIntoVisible` method and updating the selected value\n     of the scrolled item.\n\n  9. TODO: An external item is dropped into the Tree View and the item is\n     selected.\n\n 10. TODO: Lazy loading content that has selected items?\n\nThing that does not fire this event:\n  - TODO: Renaming a selected item.\n\n  - TODO: Applying a filter that keeps all selected items rendered."
                },
                "complexType": {
                    "original": "TreeViewItemModelExtended[]",
                    "resolved": "TreeViewItemModelExtended[]",
                    "references": {
                        "TreeViewItemModelExtended": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/renders/tree-view/types.ts::TreeViewItemModelExtended"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "dropItems": {
                "complexType": {
                    "signature": "(acceptDrop: boolean, dataTransferInfo: TreeViewDataTransferInfo, items?: TreeViewItemModel[]) => Promise<void>",
                    "parameters": [{
                            "name": "acceptDrop",
                            "type": "boolean",
                            "docs": ""
                        }, {
                            "name": "dataTransferInfo",
                            "type": "{ newContainer: GxDataTransferInfo; draggedItems: GxDataTransferInfo[]; draggingSelectedItems: boolean; dropInTheSameTree: boolean; }",
                            "docs": ""
                        }, {
                            "name": "items",
                            "type": "TreeViewItemModel[]",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "TreeViewDataTransferInfo": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewDataTransferInfo"
                        },
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given the drop accepting, the data transfer info and the external items,\nit process the drops of the items in the tree.",
                    "tags": []
                }
            },
            "getItemsInfo": {
                "complexType": {
                    "signature": "(itemsId: string[]) => Promise<TreeViewItemModelExtended[]>",
                    "parameters": [{
                            "name": "itemsId",
                            "type": "string[]",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "TreeViewItemModelExtended": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/renders/tree-view/types.ts::TreeViewItemModelExtended"
                        }
                    },
                    "return": "Promise<TreeViewItemModelExtended[]>"
                },
                "docs": {
                    "text": "Given a list of ids, it returns an array of the items that exists in the\ngiven list.",
                    "tags": []
                }
            },
            "loadLazyContent": {
                "complexType": {
                    "signature": "(itemId: string, items?: TreeViewItemModel[], downloading?: boolean, lazy?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "itemId",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "items",
                            "type": "TreeViewItemModel[]",
                            "docs": ""
                        }, {
                            "name": "downloading",
                            "type": "boolean",
                            "docs": ""
                        }, {
                            "name": "lazy",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given an item id, an array of items to add, the download status and the\nlazy state, updates the item's UI Model.",
                    "tags": []
                }
            },
            "removeItems": {
                "complexType": {
                    "signature": "(items: string[]) => Promise<void>",
                    "parameters": [{
                            "name": "items",
                            "type": "string[]",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "TreeViewRemoveItemsResult": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/renders/tree-view/types.ts::TreeViewRemoveItemsResult"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given a list of ids, removes the items and their children in the tree.",
                    "tags": []
                }
            },
            "reloadItems": {
                "complexType": {
                    "signature": "(itemId: string, beforeProperties?: Partial<TreeViewItemModel>, afterProperties?: Partial<TreeViewItemModel>) => Promise<boolean>",
                    "parameters": [{
                            "name": "itemId",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "beforeProperties",
                            "type": "{ id?: string; caption?: string; checkbox?: boolean; checked?: boolean; class?: string; disabled?: boolean; downloading?: boolean; dragDisabled?: boolean; dropDisabled?: boolean; editable?: boolean; expanded?: boolean; expandableButton?: \"action\" | \"no\" | \"decorative\"; lastItemId?: string; lazy?: boolean; leaf?: boolean; leftImgSrc?: string; indeterminate?: boolean; items?: TreeViewItemModel[]; metadata?: string; order?: number; render?: boolean; rightImgSrc?: string; selected?: boolean; toggleCheckboxes?: boolean; }",
                            "docs": ""
                        }, {
                            "name": "afterProperties",
                            "type": "{ id?: string; caption?: string; checkbox?: boolean; checked?: boolean; class?: string; disabled?: boolean; downloading?: boolean; dragDisabled?: boolean; dropDisabled?: boolean; editable?: boolean; expanded?: boolean; expandableButton?: \"action\" | \"no\" | \"decorative\"; lastItemId?: string; lazy?: boolean; leaf?: boolean; leftImgSrc?: string; indeterminate?: boolean; items?: TreeViewItemModel[]; metadata?: string; order?: number; render?: boolean; rightImgSrc?: string; selected?: boolean; toggleCheckboxes?: boolean; }",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "Partial": {
                            "location": "global",
                            "id": "global::Partial"
                        },
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        }
                    },
                    "return": "Promise<boolean>"
                },
                "docs": {
                    "text": "Given an item id and the additional properties to update before and after\nreload, it reloads the items of the `itemId` node by using the\n`lazyLoadTreeItemsCallback` property.",
                    "tags": []
                }
            },
            "scrollIntoVisible": {
                "complexType": {
                    "signature": "(path: string | string[], afterProperties?: Partial<TreeViewItemModel>) => Promise<boolean>",
                    "parameters": [{
                            "name": "path",
                            "type": "string | string[]",
                            "docs": ""
                        }, {
                            "name": "afterProperties",
                            "type": "{ id?: string; caption?: string; checkbox?: boolean; checked?: boolean; class?: string; disabled?: boolean; downloading?: boolean; dragDisabled?: boolean; dropDisabled?: boolean; editable?: boolean; expanded?: boolean; expandableButton?: \"action\" | \"no\" | \"decorative\"; lastItemId?: string; lazy?: boolean; leaf?: boolean; leftImgSrc?: string; indeterminate?: boolean; items?: TreeViewItemModel[]; metadata?: string; order?: number; render?: boolean; rightImgSrc?: string; selected?: boolean; toggleCheckboxes?: boolean; }",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "Partial": {
                            "location": "global",
                            "id": "global::Partial"
                        },
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        }
                    },
                    "return": "Promise<boolean>"
                },
                "docs": {
                    "text": "Given the path of the item (represent by a sorted array containing all ids\nfrom the root to the item) and the additional properties to update after,\nit displays and scrolls into the item view.\nThe path can also be a string representing the id of the item to scroll\ninto.\n\nWhen using a path, this method will fail if:\n  - The path does not start from the root element.\n  - The path contains a cycle.\n  - The path does not correspond to a valid path on the server:\n    - One of the item of the path, except for the last one, is a leaf.\n    - An item in the path does not exists on the server.\n    - The path has repeated items.\n    - And so on.",
                    "tags": []
                }
            },
            "toggleItems": {
                "complexType": {
                    "signature": "(treeItemIds: string[], expand?: boolean) => Promise<TreeViewItemExpandedInfo[]>",
                    "parameters": [{
                            "name": "treeItemIds",
                            "type": "string[]",
                            "docs": "An array id the tree items to be toggled."
                        }, {
                            "name": "expand",
                            "type": "boolean",
                            "docs": "A boolean indicating that the tree item should be expanded or collapsed. (optional)"
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "TreeViewItemExpandedInfo": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemExpandedInfo"
                        }
                    },
                    "return": "Promise<TreeViewItemExpandedInfo[]>"
                },
                "docs": {
                    "text": "This method is used to toggle a tree item by the tree item id/ids.",
                    "tags": [{
                            "name": "param",
                            "text": "treeItemIds An array id the tree items to be toggled."
                        }, {
                            "name": "param",
                            "text": "expand A boolean indicating that the tree item should be expanded or collapsed. (optional)"
                        }, {
                            "name": "returns",
                            "text": "The modified items after the method was called."
                        }]
                }
            },
            "updateAllItemsProperties": {
                "complexType": {
                    "signature": "(properties: { expanded?: boolean; checked?: boolean; }) => Promise<void>",
                    "parameters": [{
                            "name": "properties",
                            "type": "{ expanded?: boolean; checked?: boolean; }",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given a subset of item's properties, it updates all item UI models.",
                    "tags": []
                }
            },
            "updateItemsProperties": {
                "complexType": {
                    "signature": "(items: string[], properties: Partial<TreeViewItemModel>) => Promise<void>",
                    "parameters": [{
                            "name": "items",
                            "type": "string[]",
                            "docs": ""
                        }, {
                            "name": "properties",
                            "type": "{ id?: string; caption?: string; checkbox?: boolean; checked?: boolean; class?: string; disabled?: boolean; downloading?: boolean; dragDisabled?: boolean; dropDisabled?: boolean; editable?: boolean; expanded?: boolean; expandableButton?: \"action\" | \"no\" | \"decorative\"; lastItemId?: string; lazy?: boolean; leaf?: boolean; leftImgSrc?: string; indeterminate?: boolean; items?: TreeViewItemModel[]; metadata?: string; order?: number; render?: boolean; rightImgSrc?: string; selected?: boolean; toggleCheckboxes?: boolean; }",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "Partial": {
                            "location": "global",
                            "id": "global::Partial"
                        },
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../tree-view/tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemModel"
                        },
                        "Map": {
                            "location": "global",
                            "id": "global::Map"
                        },
                        "TreeViewItemModelExtended": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/renders/tree-view/types.ts::TreeViewItemModelExtended"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given a item list and the properties to update, it updates the properties\nof the items in the list.",
                    "tags": []
                }
            },
            "updateValidDropZone": {
                "complexType": {
                    "signature": "(requestTimestamp: number, newContainerId: string, draggedItems: GxDataTransferInfo[], validDrop: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "requestTimestamp",
                            "type": "number",
                            "docs": "Time where the request to the server was made. Useful to avoid having old information."
                        }, {
                            "name": "newContainerId",
                            "type": "string",
                            "docs": "ID of the container where the drag is trying to be made."
                        }, {
                            "name": "draggedItems",
                            "type": "GxDataTransferInfo[]",
                            "docs": "Information about the dragged items."
                        }, {
                            "name": "validDrop",
                            "type": "boolean",
                            "docs": "Current state of the droppable zone."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "GxDataTransferInfo": {
                            "location": "import",
                            "path": "../../../common/types",
                            "id": "src/common/types.ts::GxDataTransferInfo"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Update the information about the valid droppable zones.",
                    "tags": [{
                            "name": "param",
                            "text": "requestTimestamp Time where the request to the server was made. Useful to avoid having old information."
                        }, {
                            "name": "param",
                            "text": "newContainerId ID of the container where the drag is trying to be made."
                        }, {
                            "name": "param",
                            "text": "draggedItems Information about the dragged items."
                        }, {
                            "name": "param",
                            "text": "validDrop Current state of the droppable zone."
                        }]
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "filter",
                "methodName": "handleFilterChange"
            }, {
                "propName": "filterDebounce",
                "methodName": "handleFilterDebounceChange"
            }, {
                "propName": "filterList",
                "methodName": "handleFilterListChange"
            }, {
                "propName": "filterOptions",
                "methodName": "handleFilterOptionsChange"
            }, {
                "propName": "filterType",
                "methodName": "handleFilterTypeChange"
            }, {
                "propName": "multiSelection",
                "methodName": "handleMultiSelectionChange"
            }, {
                "propName": "treeModel",
                "methodName": "handleTreeModelChange"
            }];
    }
    static get listeners() {
        return [{
                "name": "checkboxChange",
                "method": "updateCheckboxValue",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "checkboxToggleChange",
                "method": "updateCheckboxValue",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "loadLazyContent",
                "method": "loadLazyChildrenHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "modifyCaption",
                "method": "handleCaptionModification",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "openReference",
                "method": "handleOpenReference",
                "target": undefined,
                "capture": true,
                "passive": false
            }];
    }
}
//# sourceMappingURL=tree-view-render.js.map
